import type {DesignerStateType} from '@packages/systems/core/records/DesignerState';
import type {XscpLibraryComponent} from '@packages/systems/core/records/Xscp';
import type {UiNodeStateType} from '@packages/systems/core/records/UiNodeState';
import {
  EElement,
  EList,
  ELiteral,
  EText,
  Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {
  type ExpressionPath,
  Expressions,
  Component,
  matchExpression,
  Plugins,
  ElementPreset,
  inferType,
  Type,
  Types,
  CheckerState,
} from '@packages/systems/wfdl';
import {
  isUserComponentInst,
  filterElementTree,
  isInsideCommerceCheckoutFormContainer,
  isInsideCommerceOrderConfirmationContainer,
  isInsideCommercePaypalCheckoutFormContainer,
  contains,
  isCodeIsland,
} from '@packages/utilities/platform';
import {
  isDynamoWrapper,
  isSearchResultWrapper,
  isPagination,
  isLink,
  isDynamoItem,
  isDynamicBoundOrConditioned,
  isPaginationPrevious,
  isPaginationNext,
  isRichText,
} from '@packages/utilities/elementTypeUtils';
import {
  selectCurrentComponent,
  selectCurrentComponentType,
} from '@packages/utilities/platform-selectors';
import {
  unlinkComponent,
  updateElementWithTypeChecking,
  elementHasBinding,
} from '@packages/systems/component-unlink';
import {get as jsonGet} from '@packages/utilities/json';
import {
  qualify,
  typeName,
  getName,
  getPluginName,
} from '@packages/systems/wfdl-Qualified';
import {getPluginAsset} from '@packages/shared/utils/AssetUtils';
import {AssetsType} from '@packages/systems/designer/assets';
import {getInitialChildrenForElement} from '@packages/systems/dynamo/utils/DynamoElementUtils';
import {Pair, getFirst, getSecond, pair} from '@packages/utilities/Pair';
import {Basic} from '@packages/systems/core/plugins';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
  getAnn,
  stripAll,
} from '@packages/utilities/contextual-expression';
import {generateGuid} from '@packages/utilities/guid';
import {getCurrentOrDefaultExpression} from '@packages/systems/core/component-conversions';
import {UniqueIdsState} from '@packages/utilities/platform-instantiateExpressionWithUniqueIds';
import {identity} from '@packages/utilities/fp/utils';
import {StyleBlockStateType} from '@packages/shared/LegacyStyleSystem/states/StyleBlockStateTypes';
import {parseVariantKey} from '@packages/systems/style-legacy/variants';
import {TEMP_PATH} from '@packages/utilities/expression-path-constants';

const CHILDREN_PATH: ExpressionPath = [{in: 'Record', at: 'children'}];
const PAGINATION_PATH: ExpressionPath = [{in: 'Record', at: 'pagination'}];
const DYN_PATH: ExpressionPath = [{in: 'Record', at: 'dyn'}];
const ATTR_SRC_PATH: ExpressionPath = [
  {in: 'Record', at: 'attr'},
  {in: 'Record', at: 'src'},
];
const IMG_ID_PATH: ExpressionPath = [
  {in: 'Record', at: 'img'},
  {in: 'Record', at: 'id'},
];
const DYN_COLLECTION_PATH: ExpressionPath = [
  {in: 'Record', at: 'dyn'},
  {in: 'Record', at: 'collection'},
];
const DYN_QUERY_PATH: ExpressionPath = [
  {in: 'Record', at: 'dyn'},
  {in: 'Record', at: 'query'},
];
const DYN_BIND_INNERHTML_PATH: ExpressionPath = [
  {in: 'Record', at: 'dyn'},
  {in: 'Record', at: 'bind'},
  {in: 'Record', at: 'innerHTML'},
];
const DYN_BIND_SRC_PATH: ExpressionPath = [
  {in: 'Record', at: 'dyn'},
  {in: 'Record', at: 'bind'},
  {in: 'Record', at: 'src'},
];

export type XscpElementPayload = Pair<StateType, ExpressionElement>;

type XscpElementTransformer = (
  payload: XscpElementPayload
) => XscpElementPayload;

export type XscpLog = {
  droppedLinks: number;
  dynBindRemovedCount: number;
  dynListBindRemovedCount: number;
  paginationRemovedCount: number;
  universalBindingsRemovedCount: number;
  unlinkedSymbolCount: number;
  codeComponentsRemovedCount: number;
  richTextComponentsStripped: boolean;
};

/**
 * Shares ID preparation across linked and expanded variants of one copy so switching
 * to an expanded instance preserves node identities and section links within the
 * selection.
 */
export type XscpPreparationContext = {
  // The entire original selection, including targets outside an expanded instance.
  initialElement: ExpressionElement;
  // Original IDs -> prepared IDs, reused by every variant.
  idMap: Record<string, string>;
  // Prevent a node already prepared by another variant from receiving a second ID.
  preparedNodeIds: Set<string>;
};

type StateType = {
  preparationContext?: XscpPreparationContext;
  log: XscpLog;
  idState: UniqueIdsState;
  initialElement: ExpressionElement;
  sectionLinksToReplace: Set<string>;
  styleBlockState: StyleBlockStateType;
  reverseIdMap: Record<string, string>;
};

const emptyXscpLog: XscpLog = {
  droppedLinks: 0,
  dynBindRemovedCount: 0,
  dynListBindRemovedCount: 0,
  paginationRemovedCount: 0,
  universalBindingsRemovedCount: 0,
  unlinkedSymbolCount: 0,
  codeComponentsRemovedCount: 0,
  richTextComponentsStripped: false,
};

const updateState = (
  state: StateType,
  updates: Partial<StateType>
): StateType => ({
  ...state,
  ...updates,
});

type NumericXscpLogKey = {
  [K in keyof XscpLog]: XscpLog[K] extends number ? K : never;
}[keyof XscpLog];

const incrementLogKeyInState = (
  state: StateType,
  key: NumericXscpLogKey
): StateType => {
  const initialCount = state.log[key] || 0;
  return updateState(state, {
    log: {...state.log, [key]: initialCount + 1},
  });
};

const setNextIdInState = (state: StateType, id: string): StateType =>
  updateState(state, {
    idState: {...state.idState, id},
  });

const setIdStateInState = (
  state: StateType,
  id: string,
  idMap: {[oldId: string]: string}
): StateType => {
  const existingIdMap = state.idState.idMap;
  const reverseIdMap = state.reverseIdMap;
  for (const key in idMap) {
    const val = idMap[key];
    if (val !== undefined) {
      existingIdMap[key] = val;
      reverseIdMap[val] = key;
    }
  }
  return updateState(state, {
    idState: {id, idMap: existingIdMap},
    reverseIdMap,
  });
};

const addNewSectionLinkToReplace = (
  state: StateType,
  domId: string
): StateType =>
  updateState(state, {
    sectionLinksToReplace: new Set(state.sectionLinksToReplace).add(domId),
  });

const prepareXscpElementWithTransformer =
  (
    styleBlockState: StyleBlockStateType,
    preparationContext?: XscpPreparationContext
  ) =>
  (transformer: XscpElementTransformer) =>
  (initialId: string) =>
  (
    element: ExpressionElement
  ): {element: ExpressionElement; state: StateType} => {
    const initialState: StateType = {
      log: emptyXscpLog,
      idState: {id: initialId, idMap: preparationContext?.idMap ?? {}},
      initialElement: preparationContext?.initialElement ?? element,
      preparationContext,
      sectionLinksToReplace: new Set<string>(),
      styleBlockState,
      reverseIdMap: Object.fromEntries(
        Object.entries(preparationContext?.idMap ?? {}).map(
          ([source, target]) => [target, source]
        )
      ),
    };

    const combiner =
      (expr: Expression) =>
      (state: StateType): Pair<StateType, Expression> => {
        // The transformer expects an element, so we need to check if the expression
        // in the walk is an element.
        const maybeElement = Expressions.getElement(expr);
        if (!maybeElement) {
          return pair(state, expr);
        }
        const res = transformer(pair(state, maybeElement));

        // create a full element again
        return pair(getFirst(res), EElement(getSecond(res)));
      };

    const transformedExpression = Expressions.everywhereWithState(combiner)(
      EElement(element)
    )(initialState);
    const newExpr = getSecond(transformedExpression);
    const newState = getFirst(transformedExpression);

    const newEl = Expressions.getElement(newExpr);
    if (!newEl) {
      // We should always have an element at the end of the transformation,
      // in case we don't return back the original element.
      return {element, state: initialState};
    }
    return {element: newEl, state: newState};
  };

const composeXscpElementTransformer = (
  fns: Array<XscpElementTransformer>
): XscpElementTransformer => {
  return function (payload: XscpElementPayload): XscpElementPayload {
    return fns.reduce((result, fn) => fn(result), payload);
  };
};

const prepareXscpElement = (
  designerState: DesignerStateType,
  styleBlockState: StyleBlockStateType,
  currentComponent: Component.ComponentType,
  currentComponentType: Plugins.QualifiedElement,
  getAsset: (
    pluginName: string,
    assetName: string
  ) => {
    id: string;
    url: string;
  },
  expandUserComponents: boolean,
  getLibraryComponent?: (
    element: ExpressionElement
  ) => XscpLibraryComponent | undefined,
  preparationContext?: XscpPreparationContext
) =>
  prepareXscpElementWithTransformer(
    styleBlockState,
    preparationContext
  )(
    composeXscpElementTransformer([
      updateElementId,
      ...(expandUserComponents
        ? [
            unlinkComponentInstance(
              designerState,
              currentComponentType,
              getLibraryComponent
            ),
          ]
        : []),
      stripTempData,
      removeLegacyDynamicListBinding, // This needs to be before removeLegacyDynamicBinding
      removeLegacyDynamicBinding(getAsset),
      removeAllBindings(
        designerState.plugins,
        designerState.components,
        currentComponent
      ),
      removePagination,
      removeCodeComponents,
      removeDeadLinks,
    ])
  );

const prepareXscpElementWithDynamicBindings = (
  designerState: DesignerStateType,
  styleBlockState: StyleBlockStateType,
  currentComponentType: Plugins.QualifiedElement,
  expandUserComponents: boolean,
  getLibraryComponent?: (
    element: ExpressionElement
  ) => XscpLibraryComponent | undefined,
  preparationContext?: XscpPreparationContext
) => {
  return prepareXscpElementWithTransformer(
    styleBlockState,
    preparationContext
  )(
    composeXscpElementTransformer([
      updateElementId,
      ...(expandUserComponents
        ? [
            unlinkComponentInstance(
              designerState,
              currentComponentType,
              getLibraryComponent
            ),
          ]
        : []),
      stripTempData,
      removePagination,
      removeCodeComponents,
      removeDeadLinks,
    ])
  );
};

export const unlinkComponentInstance =
  (
    designerState: DesignerStateType,
    currentComponentType: Plugins.QualifiedElement,
    getLibraryComponent?: (
      element: ExpressionElement
    ) => XscpLibraryComponent | undefined
  ) =>
  (p: XscpElementPayload): XscpElementPayload => {
    const components = designerState.components;
    const plugins = designerState.plugins;

    const element = getSecond(p);
    const state = getFirst(p);

    if (
      !isUserComponentInst(components, element) ||
      getLibraryComponent?.(element)
    ) {
      return pair(state, element);
    }

    const [unlinkedElement, unlinkIdsState, nextStyleBlockState] =
      unlinkComponent(
        components,
        element,
        plugins,
        {id: state.idState.id, idMap: {}},
        state.styleBlockState,
        currentComponentType
      );

    let updatedState = updateState(state, {
      styleBlockState: nextStyleBlockState,
    });

    if (unlinkIdsState) {
      updatedState = setIdStateInState(
        updatedState,
        unlinkIdsState.id,
        unlinkIdsState.idMap
      );
    }

    updatedState = incrementLogKeyInState(updatedState, 'unlinkedSymbolCount');

    return pair(updatedState, unlinkedElement);
  };

export const stripTempData = (p: XscpElementPayload): XscpElementPayload => {
  const element = getSecond(p);
  const state = getFirst(p);

  const elementData = Expressions.getElementData(element);
  const newElementData = Expressions.deleteInRecord(elementData, TEMP_PATH);

  // If no temp data was removed, return the original element
  if (elementData === newElementData) {
    return p;
  }

  const newElement = Expressions.setElementData(element, newElementData);

  return pair(state, newElement);
};

export const updateElementId = (p: XscpElementPayload): XscpElementPayload => {
  const element = getSecond(p);
  const state = getFirst(p);

  const elementId = element.id;
  const preparedNodeIds = state.preparationContext?.preparedNodeIds;
  const previousId = state.reverseIdMap[elementId];
  const newId = preparedNodeIds?.has(elementId)
    ? elementId
    : previousId && preparedNodeIds?.has(previousId)
      ? previousId
      : generateGuid();

  const idMap = state.idState.idMap;
  const reverseIdMap = state.reverseIdMap;

  const newEl = {
    ...element,
    id: newId,
  };

  const oldKey = reverseIdMap[elementId];
  if (oldKey !== undefined) {
    idMap[oldKey] = newId;
    reverseIdMap[newId] = oldKey;
  } else {
    idMap[elementId] = newId;
    reverseIdMap[newId] = elementId;
  }

  return pair(
    updateState(state, {idState: {id: newId, idMap}, reverseIdMap}),
    newEl
  );
};

export const removePagination = (p: XscpElementPayload): XscpElementPayload => {
  const element = getSecond(p);
  const state = getFirst(p);

  if (isDynamoWrapper(element) || isSearchResultWrapper(element)) {
    // Remove `pagination` children and `data.pagination` from the element
    const elementDataWithNoPagination = Expressions.deleteInRecord(
      Expressions.getElementData(element),
      PAGINATION_PATH
    );
    const elementWithNoPaginationChildren = filterElementTree(
      Expressions.setElementData(element, elementDataWithNoPagination),
      (el) => !isPagination(el)
    );
    return pair(
      incrementLogKeyInState(state, 'paginationRemovedCount'),
      elementWithNoPaginationChildren
    );
  }

  // If copy target is a Pagination element replace it with a div block
  if (isPagination(element)) {
    const blockElement = Expressions.getElement(
      ElementPreset.instantiateFactory(
        Basic.elementPresets.DivBlock,
        element.id
      ).element
    );

    if (!blockElement) {
      return p;
    }

    return pair(
      incrementLogKeyInState(state, 'paginationRemovedCount'),
      blockElement
    );
  }

  if (isPaginationPrevious(element) || isPaginationNext(element)) {
    const linkElement = Expressions.getElement(
      ElementPreset.instantiateFactory(
        Basic.elementPresets.LinkBlock,
        element.id
      ).element
    );
    if (!linkElement) {
      return p;
    }
    return pair(
      incrementLogKeyInState(state, 'paginationRemovedCount'),
      linkElement
    );
  }

  return p;
};

export const removeCodeComponents = (
  p: XscpElementPayload
): XscpElementPayload => {
  const element = getSecond(p);
  const state = getFirst(p);

  if (isCodeIsland(element)) {
    const blockElement = Expressions.getElement(
      ElementPreset.instantiateFactory(
        Basic.elementPresets.DivBlock,
        element.id
      ).element
    );

    if (!blockElement) {
      return p;
    }

    return pair(
      incrementLogKeyInState(state, 'codeComponentsRemovedCount'),
      blockElement
    );
  }

  return p;
};

export const removeLegacyDynamicListBinding = (
  p: XscpElementPayload
): XscpElementPayload => {
  const element = getSecond(p);
  const state = getFirst(p);

  const elementData = Expressions.getElementData(element);
  const elementHasDynCollection = Expressions.getIn(
    elementData,
    DYN_COLLECTION_PATH
  );

  if (isDynamoWrapper(element) && elementHasDynCollection) {
    const pathToDynamoItem = Expressions.findPath(elementData, (_expr) => {
      const el = Expressions.getElement(_expr);
      return Boolean(el && isDynamoItem(el));
    });

    if (!pathToDynamoItem) {
      return p;
    }
    // delete all children of dynamoitem
    const step1 = Expressions.sparseSetIn(
      elementData,
      pathToDynamoItem.concat([
        {in: 'Element'},
        {in: 'Record', at: 'children'},
      ]),
      EList([])
    );
    // delete dyn collection
    const step2 = Expressions.deleteInRecord(step1, DYN_COLLECTION_PATH);
    // delete dyn query
    const step3 = Expressions.deleteInRecord(step2, DYN_QUERY_PATH);
    const nextElement = Expressions.setElementData(element, step3);
    return pair(
      incrementLogKeyInState(state, 'dynListBindRemovedCount'),
      nextElement
    );
  }
  return p;
};

const replaceSrcBind = (
  elementData: Expression,
  getAsset: (
    pluginName: string,
    assetName: string
  ) => {
    id: string;
    url: string;
  }
) => {
  const srcBind = Expressions.getIn(elementData, DYN_BIND_SRC_PATH);
  if (srcBind) {
    const placeholder = getAsset('Basic', 'placeholder.svg');
    const step1 = Expressions.sparseSetIn(
      elementData,
      ATTR_SRC_PATH,
      EText(placeholder.url)
    );
    return Expressions.sparseSetIn(step1, IMG_ID_PATH, EText(placeholder.id));
  }
  return elementData;
};

const replaceInnerHtmlBind = (
  element: ExpressionElement,
  nextId: string
): {
  nextId: string;
  elementData: Expression;
} => {
  const elementData = Expressions.getElementData(element);

  const innerHtmlBind = Expressions.getIn(elementData, DYN_BIND_INNERHTML_PATH);
  if (innerHtmlBind) {
    const initialChildren = getInitialChildrenForElement(element, nextId);

    return {
      nextId: getFirst(initialChildren),
      elementData: Expressions.sparseSetIn(
        elementData,
        CHILDREN_PATH,
        EList(getSecond(initialChildren))
      ),
    };
  }
  return {nextId, elementData};
};

export const removeLegacyDynamicBinding =
  (
    getAsset: (
      pluginName: string,
      assetName: string
    ) => {
      id: string;
      url: string;
    }
  ) =>
  (p: XscpElementPayload): XscpElementPayload => {
    const element = getSecond(p);
    const state = getFirst(p);

    if (isDynamicBoundOrConditioned(element)) {
      // if has dyn.bind.html add in a default html in its place
      const {nextId: newNextId, elementData} = replaceInnerHtmlBind(
        element,
        state.idState.id
      );

      // if has dyn.bind.src add in a fallback placholder image to attr.src and attr.id
      const step1 = replaceSrcBind(elementData, getAsset);

      // delete dyn
      const step2 = Expressions.deleteInRecord(step1, DYN_PATH);
      const nextElement = Expressions.setElementData(element, step2);
      return pair(
        setNextIdInState(
          incrementLogKeyInState(state, 'dynBindRemovedCount'),
          newNextId
        ),
        nextElement
      );
    }

    return p;
  };

const getPropertyBindingKey = matchExpression({
  Select: ({from, prop}) => {
    if (Expressions.isDataVariable(from)) {
      return prop;
    }
    return null;
  },
  _: () => null,
});

/**
 * For a given element in order to correctly remove and replace existing bindings:
 *  - Run the Type Checker for the Element to get the current type for each piece
 *    of the element.
 *  - Replace each binding with the default value for that type.
 *  - Get back the non-contextual expression version and return that.
 */
export const removeAllBindings = (
  plugins: Plugins.PluginsType,
  components: Component.ComponentMap,
  currentComponent: Component.ComponentType
) => {
  const checkerState = Plugins.buildTypeCheckerStateWithComponents(plugins)(
    components
  )(Plugins.getEnvTypes(plugins));
  const componentDataType = Component.getDataType(currentComponent);

  return (p: XscpElementPayload): XscpElementPayload => {
    const element = getSecond(p);
    const state = getFirst(p);

    if (!elementHasBinding(element)) {
      return p;
    }

    let bindingRemoved = false;
    const updater = (
      e: ContextualExpression,
      _plugins: Plugins.PluginsType,
      componentType: Type,
      existingState: CheckerState
    ) => {
      if (ContextualExpressions.isBinding(stripAnn(e))) {
        const propFields = Types.getRecordFields(componentType);
        const type = getAnn(e).type;
        if (!type) {
          const getProp = getPropertyBindingKey(stripAll(e));
          if (getProp) {
            // Apply the default from the root component type
            const propType = propFields && propFields[getProp];
            if (propType) {
              bindingRemoved = true;
              // Reset to whatever the default is here
              const newExpression =
                getCurrentOrDefaultExpression(_plugins)(propType);

              // Because our walk function is with ContextualExpressions we need to create a new
              // ContextualExpression with the new expression
              const [, newContextualExpression] =
                inferType(newExpression)(existingState);
              return newContextualExpression;
            }
          }
          return e;
        }
        bindingRemoved = true;
        const newExpression = getCurrentOrDefaultExpression(plugins)(type);

        const [, newContextualExpression] =
          inferType(newExpression)(existingState);

        return newContextualExpression;
      }
      return e;
    };
    const nextElement = updateElementWithTypeChecking(
      componentDataType,
      element,
      updater,
      checkerState,
      plugins
    );

    if (!nextElement) {
      return p;
    }
    return pair(
      bindingRemoved
        ? incrementLogKeyInState(state, 'universalBindingsRemovedCount')
        : state,
      nextElement
    );
  };
};

const getLinkDataPojo = matchExpression({
  Record: (fields) => {
    return fields.link ? Expressions.getLiteral(fields.link)?.value : null;
  },
  _: () => null,
});

export const removeDeadLinks = (p: XscpElementPayload): XscpElementPayload => {
  const element = getSecond(p);
  const state = getFirst(p);

  const isLinkElement = isLink(element);
  const linkElementData = Expressions.getElementData(element);

  const linkData = getLinkDataPojo(linkElementData);

  if (!isLinkElement || !linkData) {
    return p;
  }

  const linkMode = jsonGet(linkData, ['mode']);

  // if element we got a link that is not pointing to a page or section
  // then do nothing
  if (linkMode !== 'page' && linkMode !== 'section') {
    return p;
  }

  // if element we got is a section link, and that section is within the tree
  // then add the element id to the sectionLinksToReplace set
  if (linkMode === 'section') {
    const domId = jsonGet(linkData, ['domId']);

    const {initialElement} = state;

    const sectionLinkInsideTree = contains(
      initialElement,
      (el) =>
        el.id === domId ||
        Boolean(
          state.preparationContext && state.idState.idMap[el.id] === domId
        )
    );

    if (sectionLinkInsideTree) {
      // We found a link we want to preserve, and eventually update to the new
      // element id
      return pair(addNewSectionLinkToReplace(state, element.id), element);
    }
  }
  // else remove the link and reset to default
  const step1 = Expressions.sparseSetIn(
    linkElementData,
    [{in: 'Record', at: 'link'}],
    ELiteral({
      name: qualify(Plugins.name('Basic'), typeName('Link')),
      value: {
        mode: 'external',
        href: '#',
      },
    })
  );

  const step2 = Expressions.sparseSetIn(
    step1,
    [
      {in: 'Record', at: 'attr'},
      {in: 'Record', at: 'href'},
    ],
    EText('#')
  );
  const nextElement = Expressions.setElementData(element, step2);
  return pair(incrementLogKeyInState(state, 'droppedLinks'), nextElement);
};

/**
 * Cross-site payloads do not retain component variant styles.
 */
const removeStyleVariants = (state: StateType): StateType => {
  const styleBlockState = state.styleBlockState;

  const updatedStyleBlocks = styleBlockState.styleBlocks.map((styleBlock) => {
    if (!styleBlock.variants || styleBlock.variants.size === 0) {
      return styleBlock;
    }

    // if a variant has a variantStateKey, remove it
    const filteredVariants = styleBlock.variants.filter(
      (_, key) => !parseVariantKey(key).variantState
    );

    return styleBlock.set('variants', filteredVariants);
  });

  return {
    ...state,
    styleBlockState: styleBlockState.set('styleBlocks', updatedStyleBlocks),
  };
};

export const remapXscpSectionLinks = (
  expression: Expression,
  idMap: Readonly<Record<string, string>>
): Expression =>
  Expressions.everywhere(
    matchExpression({
      Literal: (literal, expr) => {
        const {name, value} = literal;
        if (
          getPluginName(name) !== 'Basic' ||
          getName(name) !== 'Link' ||
          !value ||
          typeof value !== 'object' ||
          Array.isArray(value) ||
          value.mode !== 'section' ||
          typeof value.domId !== 'string'
        )
          return expr;
        const domId = idMap[value.domId];
        return domId ? ELiteral({...literal, value: {...value, domId}}) : expr;
      },
      _: identity,
    })
  )(expression);

// Strips user component instances from inside RichText elements before XSCP copy.
export const stripUserComponentsFromRichText = (
  element: ExpressionElement,
  components: Component.ComponentMap
): {element: ExpressionElement; didStrip: boolean} => {
  let didStrip = false;

  const process = (el: ExpressionElement): ExpressionElement => {
    const dataExpr = Expressions.getElementData(el);
    const children = Expressions.getIn(dataExpr, CHILDREN_PATH);
    const childrenList = children ? Expressions.getList(children) : null;

    if (!childrenList || childrenList.length === 0) {
      return el;
    }

    let childrenChanged = false;
    const newChildren: Expression[] = [];

    for (const childExpr of childrenList) {
      const childEl = Expressions.getElement(childExpr);
      if (!childEl) {
        newChildren.push(childExpr);
        continue;
      }

      if (isRichText(el) && isUserComponentInst(components, childEl)) {
        didStrip = true;
        childrenChanged = true;
        continue;
      }

      const processedChild = process(childEl);
      if (processedChild !== childEl) {
        childrenChanged = true;
        newChildren.push(EElement(processedChild));
      } else {
        newChildren.push(childExpr);
      }
    }

    if (!childrenChanged) {
      return el;
    }

    const newData = Expressions.sparseSetIn(
      dataExpr,
      CHILDREN_PATH,
      EList(newChildren)
    );
    return Expressions.setElementData(el, newData);
  };

  const result = process(element);
  return {element: result, didStrip};
};

export const prepareXscpWithElement =
  (
    designerState: DesignerStateType,
    uiNodeState: UiNodeStateType,
    styleBlockState: StyleBlockStateType,
    assets: AssetsType,
    expandUserComponents = true,
    getLibraryComponent?: (
      element: ExpressionElement
    ) => XscpLibraryComponent | undefined,
    preparationContext?: XscpPreparationContext
  ) =>
  (initialId: string) =>
  (
    initialElement: ExpressionElement
  ): {element: ExpressionElement; state: StateType} => {
    // When expanding components, strip RichText instances before the transformer
    // pipeline runs. Otherwise unlinkComponentInstance would flatten them into
    // raw elements, preventing detection on the paste side.
    // The didStrip flag is carried in XscpLog so the paste side can show a toast.
    const {element: elementForTransformers, didStrip} = expandUserComponents
      ? stripUserComponentsFromRichText(
          initialElement,
          designerState.components
        )
      : {element: initialElement, didStrip: false};

    const currentComponent = selectCurrentComponent({
      DesignerStore: designerState,
      UiNodeStore: uiNodeState,
    });
    const currentComponentType = selectCurrentComponentType({
      DesignerStore: designerState,
      UiNodeStore: uiNodeState,
    });
    const currentComponentRender = Component.getRender(currentComponent);

    // If checkout or order confirmation nodes, we do not want to strip their bindings
    const isInsideCommerceCheckoutOrOrderConfirmation = [
      isInsideCommerceCheckoutFormContainer,
      isInsideCommercePaypalCheckoutFormContainer,
      isInsideCommerceOrderConfirmationContainer,
    ].some((isInside) =>
      isInside(elementForTransformers, currentComponentRender)
    );
    // Transform the nodes for the payload, optionally unlinking components.
    const transformedResult = isInsideCommerceCheckoutOrOrderConfirmation
      ? prepareXscpElementWithDynamicBindings(
          designerState,
          styleBlockState,
          currentComponentType,
          expandUserComponents,
          getLibraryComponent,
          preparationContext
        )(initialId)(elementForTransformers)
      : prepareXscpElement(
          designerState,
          styleBlockState,
          currentComponent,
          currentComponentType,
          getPluginAsset(assets),
          expandUserComponents,
          getLibraryComponent,
          preparationContext
        )(initialId)(elementForTransformers);

    // Style variants are removed after we've prepared the element
    // because we want to preserve the variant data while walking
    // through the element tree to account for the generation of combo classes
    // for nested component instances
    const updatedState = removeStyleVariants(transformedResult.state);
    updatedState.log = {
      ...updatedState.log,
      richTextComponentsStripped: didStrip,
    };

    // If we have domIds to replace, we need to update
    // the link elements with the new ids.
    // Retained components can also carry links in arbitrary property fields.
    // Ordinary copies only need this walk when a section link was collected.
    const {
      sectionLinksToReplace,
      idState: {idMap},
    } = transformedResult.state;
    if (sectionLinksToReplace.size > 0 || getLibraryComponent) {
      const nextExpression = remapXscpSectionLinks(
        EElement(transformedResult.element),
        idMap
      );

      const nextElement =
        Expressions.getElement(nextExpression) ?? transformedResult.element;

      return {
        element: nextElement,
        state: updatedState,
      };
    }

    return {
      element: transformedResult.element,
      state: updatedState,
    };
  };
